import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase-config';

const PROMO_SECTIONS = [
  {
    id: 'moment',
    title: 'You know that moment.',
    lines: [
      'You ask AI for something. You get something unusable.',
      'You try again. Adjust the wording. Add more detail.',
      "It improves, slightly. But it's still not something you can actually use.",
      'So you either settle or do it yourself.',
      "That's not a tool problem.",
      "You've gotten good results before. You just can't explain why and you can't reliably get them again.",
    ],
  },
  {
    id: 'precision',
    title: "It's a precision problem.",
    lines: [
      "AI doesn't reward effort.",
      'It rewards precision.',
      'And precision is teachable.',
    ],
  },
  {
    id: 'difference',
    title: 'This is not another course about AI tools.',
    lines: [
      'This is training on how to control outcomes.',
      'No prompt tricks. No technical complexity. No fluff.',
      'Just a clear system for getting AI to produce what you actually need.',
      'No course can close your specific gap. Only something that can see it can.',
    ],
  },
  {
    id: 'cross',
    title: "The gap doesn't care how long you've been working.",
    lines: [
      "Whether you're building your career or protecting it, the method is the same.",
      'The professionals who close this gap earliest are the ones who define what the role looks like next.',
    ],
  },
  {
    id: 'quote',
    title: 'Founder Note',
    lines: [
      '"AI did not replace jobs. It redefined the role. This is where you learn that new role."',
      'The founder of this program is currently enrolled in the certified level, because this field does not stop moving and neither does this course.',
    ],
  },
  {
    id: 'close',
    title: 'Final Framing',
    lines: [
      'This is not a static lesson or a generic prompt library.',
      'A live AI instructor. A short assessment. And a clear picture of exactly where your gap is and what closes it.',
    ],
  },
];



export default function ProfileCreationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  
  // Explicitly decode returnTo and backTo parameters with safe fallbacks.
  const rawReturnTo = searchParams.get('returnTo');
  const rawBackTo = searchParams.get('backTo');
  const returnTo = rawReturnTo ? decodeURIComponent(rawReturnTo) : '/v2-course';
  const backTo = rawBackTo ? decodeURIComponent(rawBackTo) : '/';
  const returnPath = `${location.pathname}${location.search}`;
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    role: '',
    aiExperience: 'moderate', // 'beginner', 'moderate', 'advanced'
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function handleBack() {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(backTo);
  }

  // Restore sessionStorage draft (for post-signup return)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('om_profile_draft');
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft?.formData) setFormData((prev) => ({ ...prev, ...draft.formData }));

      sessionStorage.removeItem('om_profile_draft');
    } catch {
      // Ignore malformed draft payloads.
    }
  }, []);

  // Pre-populate from Firestore for returning logged-in users
  useEffect(() => {
    if (!currentUser) return;
    // Only pre-populate if all fields are still at default (no sessionStorage draft took over)
    getDoc(doc(db, 'users', currentUser.uid)).then((snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setFormData((prev) => ({
        firstName: data.firstName || prev.firstName,
        lastName: data.lastName || prev.lastName,
        role: data.role || prev.role,
        aiExperience: data.aiExperience || prev.aiExperience,
      }));

    }).catch(() => {
      // Non-fatal — user can still fill manually.
    });
  }, [currentUser]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return;
    }

    if (!currentUser) {
      try {
        sessionStorage.setItem('om_profile_draft', JSON.stringify({ formData }));
      } catch {
        // Non-fatal; user can still continue without draft restore.
      }
      // returnTo here is the final destination after assessment (e.g. /chat),
      // NOT the profile page URL — so signup lands the user directly in the assessment.
      navigate(`/signup?returnTo=${encodeURIComponent(returnTo)}`, {
        state: { backgroundLocation: location },
      });
      return;
    }

    setLoading(true);
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      
      await updateDoc(userDocRef, {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        role: formData.role.trim(),
        aiExperience: formData.aiExperience,
        profileCompletedAt: new Date().toISOString(),
      }).catch(async (err) => {
        if (err.code === 'not-found') {
          await setDoc(userDocRef, {
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            role: formData.role.trim(),
            aiExperience: formData.aiExperience,
            profileCompletedAt: new Date().toISOString(),
          });
        } else {
          throw err;
        }
      });

      // Navigate back with profile complete flag while preserving existing query params.
      const separator = returnTo.includes('?') ? '&' : '?';
      const finalPath = `${returnTo}${separator}profileReady=true`;
      navigate(finalPath);
    } catch (err) {
      console.error('Profile save error:', err);
      setError(err.message || 'Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleChange(field, value) {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <button onClick={handleBack} style={styles.backBtn}>← Back</button>
        
        <div style={styles.header}>
          <h1 style={styles.heading}>What should I call you?</h1>
          <p style={styles.intro}>
            Your instructor will address you by name throughout the assessment.
          </p>
          {!currentUser && (
            <p style={styles.requiredHint}>You'll create a free account on the next step. Email/password signup requires inbox validation before access is activated.</p>
          )}
        </div>

        <section style={styles.promoPanel}>
          <h2 style={styles.promoHeading}>Operator Method Narrative</h2>
          <p style={styles.promoLead}>
            Full promo copy is now visible in this active step so users see the whole context before assessment routing.
          </p>
          {PROMO_SECTIONS.map((section) => (
            <div key={section.id} style={styles.promoSection}>
              <h3 style={styles.promoSectionTitle}>{section.title}</h3>
              <div style={styles.promoLinesWrap}>
                {section.lines.map((line) => (
                  <p key={line} style={styles.promoLine}>{line}</p>
                ))}
              </div>
            </div>
          ))}
        </section>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.section}>
          {/* First Name */}
          <div style={styles.formGroup}>
            <label htmlFor="firstName" style={styles.label}>
              First Name *
            </label>
            <input
              id="firstName"
              type="text"
              placeholder="Enter your first name"
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              style={styles.input}
              disabled={loading}
              required
            />
            <p style={styles.fieldHint}>Required to continue.</p>
          </div>

          {/* Error */}
          {error && (
            <div style={styles.errorBox}>
              <p style={styles.errorText}>{error}</p>
            </div>
          )}
          </div>



          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.primaryBtn,
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'One moment…' : (currentUser ? 'Start my assessment →' : 'Continue →')}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0a0e1a',
    fontFamily: "'Inter', -apple-system, sans-serif",
    color: '#e2e8f0',
    padding: '40px 24px 80px',
  },
  container: {
    maxWidth: '560px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#6b7a99',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '0 0 20px 0',
    fontWeight: 500,
  },
  subheading: {
    fontSize: '12px',
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: '#4f8ef7',
    fontWeight: 600,
    marginBottom: '12px',
  },
  heading: {
    fontSize: '32px',
    fontWeight: 800,
    lineHeight: 1.2,
    marginBottom: '16px',
    background: 'linear-gradient(135deg, #f0f4ff 30%, #4f8ef7)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  intro: {
    fontSize: '15px',
    color: '#94a3b8',
    lineHeight: 1.6,
    marginBottom: '8px',
  },
  requiredHint: {
    fontSize: '12px',
    color: '#64748b',
    margin: 0,
  },
  optionalTag: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: 400,
  },
  launchHint: {
    fontSize: '14px',
    color: '#7a94b0',
    lineHeight: 1.65,
    marginBottom: '16px',
    textAlign: 'center',
  },
  form: {
    marginBottom: '32px',
  },
  promoPanel: {
    marginBottom: '24px',
    border: '1px solid rgba(79,142,247,0.24)',
    borderRadius: '12px',
    background: 'rgba(15, 23, 42, 0.5)',
    padding: '16px',
  },
  promoHeading: {
    margin: '0 0 8px',
    fontSize: '18px',
    color: '#dce7f7',
  },
  promoLead: {
    margin: '0 0 14px',
    fontSize: '13px',
    lineHeight: 1.6,
    color: '#9caac0',
  },
  promoSection: {
    paddingTop: '12px',
    marginTop: '12px',
    borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  promoSectionTitle: {
    margin: '0 0 8px',
    fontSize: '14px',
    color: '#e2e8f0',
    fontWeight: 700,
  },
  promoLinesWrap: {
    display: 'grid',
    gap: '8px',
  },
  promoLine: {
    margin: 0,
    fontSize: '13px',
    lineHeight: 1.6,
    color: '#cbd5e1',
  },
  section: {
    marginBottom: '18px',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '10px',
    background: 'rgba(255,255,255,0.02)',
    padding: '14px',
  },
  sectionHeading: {
    margin: '0 0 8px',
    fontSize: '17px',
    color: '#dce7f7',
  },
  sectionLead: {
    margin: '0 0 10px',
    fontSize: '13px',
    color: '#9caac0',
  },
  formGroup: {
    marginBottom: '24px',
  },
  optionsWrap: {
    display: 'grid',
    gap: '8px',
    marginBottom: '12px',
  },
  option: {
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    padding: '10px 10px',
    display: 'grid',
    gridTemplateColumns: '18px 1fr',
    gap: '8px',
    alignItems: 'start',
    cursor: 'pointer',
    backgroundColor: 'rgba(255,255,255,0.01)',
  },
  optionActive: {
    border: '1px solid rgba(98,166,255,0.6)',
    backgroundColor: 'rgba(98,166,255,0.08)',
  },
  optionLabel: {
    fontSize: '13px',
    color: '#e8eef9',
    marginBottom: '1px',
  },
  optionDetail: {
    fontSize: '12px',
    lineHeight: 1.5,
    color: '#9caac0',
  },
  contextLabel: {
    display: 'block',
    fontSize: '12px',
    color: '#9caac0',
    marginBottom: '6px',
  },
  contextInput: {
    width: '100%',
    border: '1px solid rgba(255,255,255,0.15)',
    backgroundColor: '#0f131a',
    color: '#dfe8f8',
    borderRadius: '8px',
    padding: '8px 10px',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  toggleBtn: {
    background: 'none',
    border: '1px solid rgba(79,142,247,0.3)',
    color: '#4f8ef7',
    padding: '10px 12px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    width: '100%',
    textAlign: 'left',
  },
  questionsList: {
    marginTop: '10px',
    padding: '12px',
    background: 'rgba(15,23,42,0.5)',
    borderRadius: '8px',
    border: '1px solid rgba(79,142,247,0.15)',
  },
  ol: {
    margin: 0,
    paddingLeft: '18px',
    fontSize: '13px',
    lineHeight: 1.65,
    color: '#cbd5e1',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 600,
    color: '#e2e8f0',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    color: '#e2e8f0',
    fontSize: '14px',
    fontFamily: "inherit",
    boxSizing: 'border-box',
    transition: 'all 0.2s ease',
  },
  fieldHint: {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '6px',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '6px',
    transition: 'background 0.2s ease',
  },
  radio: {
    marginTop: '2px',
    cursor: 'pointer',
  },
  radioText: {
    fontSize: '14px',
    color: '#cbd5e1',
  },
  primaryBtn: {
    width: '100%',
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #4f8ef7, #2563eb)',
    border: 'none',
    borderRadius: '8px',
    color: '#fafafa',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: '10px',
    transition: 'all 0.3s ease',
  },
  secondaryBtn: {
    width: '100%',
    padding: '12px 24px',
    background: 'none',
    border: '1px solid rgba(79,142,247,0.4)',
    borderRadius: '8px',
    color: '#4f8ef7',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  errorBox: {
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
  },
  errorText: {
    color: '#f87171',
    fontSize: '13px',
    margin: 0,
  },
  infoBox: {
    background: 'rgba(79,142,247,0.08)',
    border: '1px solid rgba(79,142,247,0.2)',
    borderRadius: '8px',
    padding: '20px',
  },
  infoTitle: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#4f8ef7',
    marginBottom: '8px',
    margin: 0,
  },
  infoList: {
    fontSize: '13px',
    color: '#cbd5e1',
    lineHeight: 1.8,
    margin: '0',
    paddingLeft: '20px',
  },
};
